// FinanceProjects — the board review/approve surface for capital / non-recurring
// PROJECTS. RJ's ask: "Projects should include a way for people to create, edit,
// remove them with the ability for the Board to review/approve Monday."
//
// Three things on one board-simple page:
//   1. A KPI strip — total projects, proposed (count + $), approved (count + $),
//      with a by-committee rollup underneath. All from the API.
//   2. The projects table — name, committee, amount, priority, status badge,
//      requested_by, with per-row Edit / Delete and Approve / Reject (the last two
//      gated server-side to approve_finance; the UI surfaces a 403 if you can't).
//   3. An "Add project" form + an edit modal — name, committee (the 8 FY27
//      committees), description, amount, priority, requested_by.
//
// The approve flow mirrors the FY27 committee approve (finance-fy27-api): an Entra
// user picker (useUserDirectory, default the current user) + an optional board
// motion reference; approving LOCKS the project (approved badge + approver + date),
// and an approver can Reopen it. Numbers come from /api/finance/projects/manage —
// nothing is hardcoded. Board/staff (view_finance); approvals need approve_finance.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  useProjectsManage,
  useUserDirectory,
  createProject,
  saveProject,
  deleteProject,
  approveProject,
  rejectProject,
  reopenProject,
} from "../hooks/useFinance";
import type { ProjectRow } from "../hooks/useFinance";
import { useAuth } from "../hooks/useAuth";
import { useSaveStatus } from "../hooks/useSaveStatus";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import InfoTip from "../components/shared/InfoTip";
import PageIntro from "../components/shared/PageIntro";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import { asNum, usd0, usdC } from "../components/finance/charts";
import { TH, THR, BTN_BRAND, FetchErrorBanner } from "../components/finance/ui";

const PRIORITIES = ["low", "medium", "high"];

const STATUS_CLR: Record<string, string> = {
  proposed: "bg-status-amber/15 text-status-amber",
  approved: "bg-status-green/15 text-status-green",
  rejected: "bg-status-red/15 text-status-red",
  deferred: "bg-theme-overlay text-theme-muted",
  in_progress: "bg-status-amber/15 text-status-amber",
  complete: "bg-status-green/15 text-status-green",
};
const PRIORITY_CLR: Record<string, string> = {
  high: "bg-status-red/15 text-status-red",
  medium: "bg-status-amber/15 text-status-amber",
  low: "bg-theme-overlay text-theme-muted",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize ${STATUS_CLR[status] ?? "bg-theme-overlay text-theme-muted"}`}
    >
      {status === "approved" ? "✓ " : ""}
      {status.replace("_", " ")}
    </span>
  );
}

export default function FinanceProjects() {
  const { data, isLoading, refresh, error } = useProjectsManage();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ProjectRow | null>(null);
  const createStatus = useSaveStatus();

  const projects = data?.projects ?? [];
  const committees = data?.committees ?? [];
  const byStatus = data?.by_status ?? {};

  const proposed = byStatus.proposed ?? { count: 0, amount: 0 };
  const approved = byStatus.approved ?? { count: 0, amount: 0 };
  const committeeRows = useMemo(
    () =>
      Object.entries(data?.by_committee ?? {})
        .map(([committee, v]) => ({ committee, ...v }))
        .sort((a, b) => b.amount - a.amount),
    [data],
  );

  async function add(form: Parameters<typeof createProject>[0]) {
    const ok = await createStatus.run(
      () => createProject(form),
      "create project",
    );
    if (ok) {
      await refresh();
      setAdding(false);
    }
  }

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title={
            <span className="inline-flex items-center gap-1.5">
              Projects <InfoTip term="capital_outlay" />
            </span>
          }
          subtitle="Create, edit, and remove projects — and the surface the Board uses to review and approve them. Proposed projects await board sign-off; approving locks the record with the approver and date."
          headerRight={
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setAdding((v) => !v);
              }}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium ${BTN_BRAND}`}
            >
              {adding ? "Cancel" : "+ Add project"}
            </button>
          }
        />
        <PageIntro>
          Each project carries an owner (committee), an estimated cost, and a
          priority. The Board reviews the proposed list, then approves, rejects,
          or defers each one — the decision is captured for the record
          (FA.PL.001 §5.5).
        </PageIntro>
      </div>

      <FetchErrorBanner error={error} what="projects" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Total Projects"
          value={data?.total_count ?? 0}
          subtitle={usdC(data?.total_amount ?? 0)}
          accent="neutral"
          isLoading={isLoading}
        />
        <KPICard
          label="Awaiting Approval"
          value={proposed.count}
          subtitle={`${usdC(proposed.amount)} proposed`}
          accent={proposed.count > 0 ? "amber" : "green"}
          isLoading={isLoading}
        />
        <KPICard
          label="Board-Approved"
          value={approved.count}
          subtitle={`${usdC(approved.amount)} approved`}
          accent="green"
          isLoading={isLoading}
        />
        <KPICard
          label="Committees"
          value={committeeRows.length}
          subtitle="With at least one project"
          accent="neutral"
          isLoading={isLoading}
        />
      </div>

      {adding && (
        <div className="space-y-2">
          {createStatus.error && (
            <p className="text-xs text-status-red">{createStatus.error}</p>
          )}
          <ProjectForm
            committees={committees}
            busy={createStatus.saving}
            submitLabel="Add project"
            onSubmit={add}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {committeeRows.length > 0 && (
        <ChartCard
          title="By Committee"
          subtitle="Total estimated cost of projects owned by each committee."
          minHeight={80}
        >
          <div className="overflow-x-auto -mx-4 md:-mx-5">
            <table className="w-full min-w-[420px] text-left">
              <thead>
                <tr className="border-b border-theme-subtle">
                  <th scope="col" className={TH}>
                    Committee
                  </th>
                  <th scope="col" className={`${TH} text-center`}>
                    Projects
                  </th>
                  <th scope="col" className={THR}>
                    Estimated
                  </th>
                </tr>
              </thead>
              <tbody>
                {committeeRows.map((r) => (
                  <tr
                    key={r.committee}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="px-3 py-2 text-sm text-theme-primary">
                      {r.committee}
                    </td>
                    <td className="px-3 py-2 text-sm text-center text-theme-secondary tabular-nums">
                      {r.count}
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                      {usd0(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}

      <ChartCard
        title="All Projects"
        subtitle="Review each project, then Approve / Reject / Defer for the board record. Edit or remove proposed projects; approved ones are locked until reopened."
        empty={!isLoading && projects.length === 0}
        minHeight={120}
      >
        <div className="overflow-x-auto -mx-4 md:-mx-5">
          <table className="w-full min-w-[1040px] text-left">
            <caption className="sr-only">
              Capital and non-recurring projects for board review
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  Project
                </th>
                <th scope="col" className={TH}>
                  Committee
                </th>
                <th scope="col" className={THR}>
                  Amount
                </th>
                <th scope="col" className={`${TH} text-center`}>
                  Priority
                </th>
                <th scope="col" className={`${TH} text-center`}>
                  Status
                </th>
                <th scope="col" className={TH}>
                  Requested by
                </th>
                <th scope="col" className={`${TH} text-right`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr
                      key={i}
                      className="hover:bg-theme-overlay/50 transition-colors"
                    >
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-3 py-2">
                          <div
                            className="skeleton h-4 rounded-md"
                            style={{
                              width: `${44 + ((i * 7 + j * 11) % 60)}px`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : projects.map((p) => (
                    <ProjectRowEl
                      key={p.id}
                      p={p}
                      onChanged={refresh}
                      onEdit={() => {
                        setAdding(false);
                        setEditing(p);
                      }}
                    />
                  ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <p className="text-xs text-theme-muted">
        Approve / Reject / Reopen are board actions (require finance-approval
        permission); if you can't take them the page will tell you. Approving
        locks a project's core fields and records the approver and date for the
        board minutes (FA.PL.001 §5.5).
      </p>

      {editing && (
        <EditModal
          project={editing}
          committees={committees}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </PageShell>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────
function ProjectRowEl({
  p,
  onChanged,
  onEdit,
}: {
  p: ProjectRow;
  onChanged: () => void;
  onEdit: () => void;
}) {
  const { user } = useAuth();
  const { data: directory } = useUserDirectory();
  const saveStatus = useSaveStatus();
  const [showApprove, setShowApprove] = useState(false);
  const [approver, setApprover] = useState("");
  const [motionRef, setMotionRef] = useState("");
  const users = directory?.users ?? [];
  const locked = p.status === "approved";

  useEffect(() => {
    if (!approver && user?.email) setApprover(user.email);
  }, [approver, user?.email]);

  async function doApprove() {
    const ok = await saveStatus.run(
      () =>
        approveProject(p.id, {
          approved_by: approver || undefined,
          board_motion_ref: motionRef.trim() || undefined,
        }),
      "approve project",
    );
    if (ok) {
      setShowApprove(false);
      onChanged();
    }
  }
  async function doReject() {
    const ok = await saveStatus.run(
      () => rejectProject(p.id),
      "reject project",
    );
    if (ok) onChanged();
  }
  async function doReopen() {
    const ok = await saveStatus.run(
      () => reopenProject(p.id),
      "reopen project",
    );
    if (ok) onChanged();
  }
  async function remove() {
    if (
      typeof window !== "undefined" &&
      !window.confirm(`Delete project "${p.name}"? This cannot be undone.`)
    )
      return;
    const ok = await saveStatus.run(
      () => deleteProject(p.id),
      "delete project",
    );
    if (ok) onChanged();
  }

  return (
    <>
      <tr className="hover:bg-theme-overlay/50 transition-colors align-top">
        <td className="px-3 py-2 text-sm text-theme-primary max-w-[280px]">
          <div className="font-medium">{p.name}</div>
          {p.description && (
            <div
              className="text-[11px] text-theme-muted truncate"
              title={p.description}
            >
              {p.description}
            </div>
          )}
          {locked && (p.approved_by || p.approved_at) && (
            <div className="text-[11px] text-status-green mt-0.5">
              Approved{p.approved_at ? ` ${p.approved_at.slice(0, 10)}` : ""}
              {p.approved_by ? ` by ${p.approved_by}` : ""}
              {p.board_motion_ref ? ` · ${p.board_motion_ref}` : ""}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-xs text-theme-secondary">
          {p.committee ?? <span className="text-theme-muted/60">—</span>}
        </td>
        <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
          {usd0(asNum(p.cost_estimate))}
        </td>
        <td className="px-3 py-2 text-center">
          {p.priority ? (
            <span
              className={`px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize ${PRIORITY_CLR[p.priority] ?? "bg-theme-overlay text-theme-muted"}`}
            >
              {p.priority}
            </span>
          ) : (
            <span className="text-theme-muted/60 text-xs">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-center">
          <StatusBadge status={p.status} />
        </td>
        <td className="px-3 py-2 text-xs text-theme-secondary">
          {p.requested_by ?? <span className="text-theme-muted/60">—</span>}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1.5">
            {!locked && (
              <>
                <button
                  type="button"
                  onClick={onEdit}
                  className="px-2 py-1 rounded-md text-xs font-medium border border-theme-subtle text-theme-secondary hover:bg-theme-overlay transition-colors"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setShowApprove((v) => !v)}
                  className={`px-2 py-1 rounded-md text-xs font-semibold ${BTN_BRAND}`}
                >
                  Approve
                </button>
                {p.status !== "rejected" && (
                  <button
                    type="button"
                    onClick={doReject}
                    className="px-2 py-1 rounded-md text-xs font-medium border border-theme-subtle text-status-red hover:bg-status-red/10 transition-colors"
                  >
                    Reject
                  </button>
                )}
                <button
                  type="button"
                  onClick={remove}
                  title="Delete project"
                  className="px-1.5 py-1 rounded-md text-theme-muted/60 hover:text-status-red transition-colors text-sm"
                >
                  ✕
                </button>
              </>
            )}
            {locked && (
              <button
                type="button"
                onClick={doReopen}
                className="px-2 py-1 rounded-md text-xs font-medium border border-theme-subtle text-theme-secondary hover:bg-theme-overlay transition-colors"
              >
                Reopen
              </button>
            )}
          </div>
          {saveStatus.error && (
            <div className="text-[11px] text-status-red mt-1 text-right">
              {saveStatus.error}
            </div>
          )}
        </td>
      </tr>
      {showApprove && !locked && (
        <tr className="border-b border-theme-subtle/50 bg-theme-overlay/30">
          <td colSpan={7} className="px-3 py-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-theme-muted">
                Approver (recorded for the board minutes)
                <select
                  value={approver}
                  onChange={(e) => setApprover(e.target.value)}
                  className="mt-1 block w-56 px-2 py-1.5 text-sm rounded-md bg-theme-surface border border-theme-subtle text-theme-secondary focus:border-dcvfd-accent focus:outline-none"
                >
                  {user?.email &&
                    !users.some((u) => u.email === user.email) && (
                      <option value={user.email}>
                        {user.name || user.email} (you)
                      </option>
                    )}
                  {users.map((u) => (
                    <option key={u.id} value={u.email}>
                      {u.name || u.email}
                      {user?.email === u.email ? " (you)" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-theme-muted">
                Board motion reference (optional)
                <input
                  value={motionRef}
                  onChange={(e) => setMotionRef(e.target.value)}
                  placeholder="e.g. BOD 2026-06-15 motion 4"
                  className="mt-1 block w-64 px-2 py-1.5 text-sm rounded-md bg-theme-surface border border-theme-subtle text-theme-primary focus:border-dcvfd-accent focus:outline-none"
                />
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={saveStatus.saving}
                  onClick={doApprove}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-40 ${BTN_BRAND}`}
                >
                  {saveStatus.saving ? "Approving…" : "Confirm approval"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowApprove(false)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium border border-theme-subtle text-theme-secondary hover:bg-theme-overlay transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Add / Edit form ───────────────────────────────────────────────────────────
interface FormValues {
  name: string;
  committee?: string | null;
  category?: string;
  cost_estimate?: number;
  priority?: string | null;
  requested_by?: string;
  description?: string;
}

function ProjectForm({
  committees,
  busy,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  committees: string[];
  busy: boolean;
  initial?: ProjectRow;
  submitLabel: string;
  onSubmit: (v: FormValues) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [committee, setCommittee] = useState(initial?.committee ?? "");
  const [cost, setCost] = useState(
    initial?.cost_estimate != null
      ? String(Math.round(asNum(initial.cost_estimate)))
      : "",
  );
  const [priority, setPriority] = useState(initial?.priority ?? "");
  const [requestedBy, setRequestedBy] = useState(initial?.requested_by ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const valid = name.trim().length > 0;

  return (
    <div className="glass rounded-2xl p-4 md:p-5 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <label className="text-xs text-theme-muted sm:col-span-2">
          Project name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Station kitchen renovation"
            className="mt-1 w-full px-3 py-2 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none text-theme-primary"
          />
        </label>
        <label className="text-xs text-theme-muted">
          Committee (owner)
          <select
            value={committee ?? ""}
            onChange={(e) => setCommittee(e.target.value)}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none text-theme-primary"
          >
            <option value="">— None —</option>
            {committees.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-theme-muted">
          Estimated cost ($)
          <input
            type="number"
            min={0}
            step={500}
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="0"
            className="mt-1 w-full px-3 py-2 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none text-theme-primary tabular-nums"
          />
        </label>
        <label className="text-xs text-theme-muted">
          Priority
          <select
            value={priority ?? ""}
            onChange={(e) => setPriority(e.target.value)}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none text-theme-primary capitalize"
          >
            <option value="">— None —</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-theme-muted">
          Requested by
          <input
            value={requestedBy}
            onChange={(e) => setRequestedBy(e.target.value)}
            placeholder="Name or committee"
            className="mt-1 w-full px-3 py-2 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none text-theme-primary"
          />
        </label>
        <label className="text-xs text-theme-muted sm:col-span-2 lg:col-span-3">
          Description (board-facing)
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Why this matters for the budget conversation…"
            className="mt-1 w-full px-3 py-2 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none text-theme-primary"
          />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm font-medium border border-theme-subtle text-theme-secondary hover:bg-theme-overlay transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() =>
            onSubmit({
              name: name.trim(),
              committee: committee || null,
              cost_estimate: Number(cost) || 0,
              priority: priority || null,
              requested_by: requestedBy.trim() || undefined,
              description: description.trim() || undefined,
            })
          }
          className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40 ${BTN_BRAND}`}
        >
          {busy ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}

function EditModal({
  project,
  committees,
  onClose,
  onSaved,
}: {
  project: ProjectRow;
  committees: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const saveStatus = useSaveStatus();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // Escape to close + a basic focus trap (Tab/Shift+Tab cycles within the
  // dialog), matching the pattern established by FinanceVendors' PoModal.
  useEffect(() => {
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save(v: FormValues) {
    const ok = await saveStatus.run(
      () =>
        saveProject(project.id, {
          name: v.name,
          committee: v.committee ?? null,
          cost_estimate: v.cost_estimate ?? 0,
          priority: v.priority ?? null,
          requested_by: v.requested_by ?? "",
          description: v.description ?? "",
        }),
      "save project",
    );
    if (ok) onSaved();
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-project-modal-title"
        className="glass rounded-2xl w-full max-w-2xl mt-12 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2
            id="edit-project-modal-title"
            className="text-sm font-semibold text-theme-primary"
          >
            Edit project — {project.name}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close edit project dialog"
            className="text-theme-muted hover:text-theme-primary text-lg leading-none"
          >
            ✕
          </button>
        </div>
        {saveStatus.error && (
          <p className="text-xs text-status-red mb-2">{saveStatus.error}</p>
        )}
        <ProjectForm
          committees={committees}
          busy={saveStatus.saving}
          initial={project}
          submitLabel="Save changes"
          onSubmit={save}
          onCancel={onClose}
        />
      </div>
    </div>
  );
}
